import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Clock, Loader2, CheckCircle2 } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { apiError } from "@/lib/format";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [signup, setSignup] = useState({ company_name: "", admin_name: "", username: "", password: "" });
  const [signupDone, setSignupDone] = useState(false);

  useEffect(() => {
    if (user) navigate("/app", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(username.trim(), password.trim(), remember);
      navigate("/app", { replace: true });
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const submitSignup = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await api.post("/auth/signup", {
        company_name: signup.company_name.trim(),
        admin_name: signup.admin_name.trim(),
        username: signup.username.trim(),
        password: signup.password.trim(),
      });
      setSignupDone(true);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex items-center justify-center p-6 sm:p-12 bg-white">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-3 mb-10">
            <img src="/teamtempus-logo.png" alt="Teamtempus" className="h-12 w-12 object-contain" />
            <div>
              <div className="text-xl font-extrabold tracking-tight text-slate-900">Teamtempus</div>
              <div className="text-xs text-slate-400">Zeiterfassung & Auftragsverwaltung</div>
            </div>
          </div>

          {signupDone ? (
            <div data-testid="signup-success">
              <div className="h-12 w-12 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
                <CheckCircle2 className="h-7 w-7 text-emerald-600" />
              </div>
              <h1 className="text-2xl font-bold tracking-tight text-slate-900 mb-1">Konto erstellt</h1>
              <p className="text-sm text-slate-500 mb-8">Dein Konto wird vom Betreiber freigeschaltet. Du kannst dich anmelden, sobald es aktiviert wurde.</p>
              <Button onClick={() => { setSignupDone(false); setMode("login"); setSignup({ company_name: "", admin_name: "", username: "", password: "" }); }} data-testid="back-to-login" className="w-full brand-bg text-white hover:opacity-90">Zur Anmeldung</Button>
            </div>
          ) : mode === "signup" ? (
            <>
              <h1 className="text-2xl font-bold tracking-tight text-slate-900 mb-1">Neue Firma registrieren</h1>
              <p className="text-sm text-slate-500 mb-8">Lege dein Firmenkonto an – Freischaltung durch den Betreiber.</p>
              <form onSubmit={submitSignup} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="company_name">Firmenname</Label>
                  <Input id="company_name" data-testid="signup-company" value={signup.company_name} onChange={(e) => setSignup({ ...signup, company_name: e.target.value })} placeholder="Muster GmbH" required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="admin_name">Ihr Name</Label>
                  <Input id="admin_name" data-testid="signup-admin-name" value={signup.admin_name} onChange={(e) => setSignup({ ...signup, admin_name: e.target.value })} placeholder="Max Mustermann" required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup_username">Benutzername</Label>
                  <Input id="signup_username" data-testid="signup-username" value={signup.username} onChange={(e) => setSignup({ ...signup, username: e.target.value })} placeholder="max" autoCapitalize="none" autoCorrect="off" spellCheck={false} required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup_password">Passwort</Label>
                  <Input id="signup_password" type="password" data-testid="signup-password" value={signup.password} onChange={(e) => setSignup({ ...signup, password: e.target.value })} placeholder="mind. 4 Zeichen" required />
                </div>
                {error && <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2" data-testid="signup-error">{error}</div>}
                <Button type="submit" disabled={busy} data-testid="signup-submit" className="w-full brand-bg text-white hover:opacity-90">
                  {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Firma registrieren
                </Button>
              </form>
              <button onClick={() => { setMode("login"); setError(""); }} data-testid="to-login" className="mt-6 text-sm text-slate-500 hover:text-slate-900">
                Zurück zur Anmeldung
              </button>
            </>
          ) : (
            <>
              <h1 className="text-2xl font-bold tracking-tight text-slate-900 mb-1">Anmelden</h1>
              <p className="text-sm text-slate-500 mb-8">Willkommen zurück im Team-Portal.</p>

              <form onSubmit={submit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="username">Benutzername</Label>
                  <Input
                    id="username"
                    data-testid="login-username-input"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="z. B. anna"
                    autoComplete="username"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Passwort</Label>
                  <Input
                    id="password"
                    type="password"
                    data-testid="login-password-input"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    autoComplete="current-password"
                    required
                  />
                </div>

                {error && (
                  <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2" data-testid="login-error">
                    {error}
                  </div>
                )}

                <label className="flex items-center gap-2 text-sm text-slate-600 select-none cursor-pointer">
                  <Checkbox checked={remember} onCheckedChange={(v) => setRemember(!!v)} data-testid="login-remember" />
                  Angemeldet bleiben
                </label>

                <Button type="submit" disabled={busy} data-testid="login-submit-button" className="w-full brand-bg text-white hover:opacity-90">
                  {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Anmelden
                </Button>
              </form>

              <div className="mt-8 pt-6 border-t border-slate-100 text-center">
                <span className="text-sm text-slate-500">Noch kein Konto? </span>
                <button onClick={() => { setMode("signup"); setError(""); }} data-testid="to-signup" className="text-sm font-semibold brand-text hover:underline">
                  Neue Firma registrieren
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="hidden lg:flex relative bg-slate-50 overflow-hidden flex-col justify-between p-12">
        <div className="absolute -top-24 -right-24 h-80 w-80 rounded-full bg-slate-200/50 blur-3xl" />
        <div className="absolute -bottom-24 -left-24 h-80 w-80 rounded-full bg-slate-200/40 blur-3xl" />
        <div className="relative flex-1 flex items-center justify-center">
          <img src="/teamtempus-logo.png" alt="Teamtempus Logo" className="w-[26rem] max-w-[80%] object-contain drop-shadow-xl" data-testid="hero-logo" />
        </div>
        <div className="relative">
          <h2 className="text-3xl font-bold tracking-tight max-w-md text-slate-900">Ihr Team, Ihre Zeit – alles an einem Ort.</h2>
          <p className="mt-3 text-slate-500 max-w-md">
            Zeiterfassung, Schichtplanung und Baustellenverwaltung im Browser – als Ergänzung zur Teamtempus Mobile-App.
          </p>
        </div>
      </div>
    </div>
  );
}
